import { useEffect, useMemo, useState } from 'react';
import { BarChart, ChevronDown, Loader2, Check, Star } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { useToast } from '../contexts/ToastContext';
import { useN8nWebhook } from '../hooks/useN8nWebhook';
import { useActiveAnalysisModels } from '../queries/analysisModels';
import {
  AnalysisModel,
  ANALYSIS_SLOTS,
  buildModelWebhookUrl,
  n8nOriginFromUrl,
  sortedActiveModels,
  defaultModel,
} from '../lib/analysis-models';

export interface ModelSelection {
  modelId: number | null; // null = use the default (first preferred) model
  slot: number;
}

interface Props {
  trackId: string;
  meetingDate: string; // yyyy-MM-dd
  raceNumber?: number; // omit for whole-meeting runs
  className?: string;
  // Controlled mode: when `selection` is provided the component reflects it and
  // reports changes via onSelectionChange instead of keeping internal state.
  // Used on the race drill-down page so a parent control can drive every race.
  selection?: ModelSelection;
  onSelectionChange?: (modelId: number, slot: number) => void;
  runVerbOverride?: string;
}

const LS_KEY = 'modelRun:last'; // { modelId, slot } — uncontrolled mode only

// The n8n origin. Prefer VITE_N8N_URL (the shared server var racing-react /
// platinum-react already set on every environment); fall back to the old admin's
// full-webhook VITE_N8N_ANALYSIS_URL. Only the origin is used — the per-model
// /webhook/<path> comes from the Analysis Models registry.
export function resolveN8nBase(): string | null {
  return n8nOriginFromUrl(import.meta.env.VITE_N8N_URL || import.meta.env.VITE_N8N_ANALYSIS_URL);
}

export function ModelRunControl({
  trackId,
  meetingDate,
  raceNumber,
  className,
  selection,
  onSelectionChange,
  runVerbOverride,
}: Props) {
  const controlled = selection !== undefined;
  const { data: models = [] } = useActiveAnalysisModels();
  const [open, setOpen] = useState(false);
  const [internalId, setInternalId] = useState<number | null>(null);
  const [internalSlot, setInternalSlot] = useState<number>(1);
  const { triggerWebhook, isLoading, message } = useN8nWebhook();
  const { error: toastError, success } = useToast();

  const active = useMemo(() => sortedActiveModels(models), [models]);
  const fallback = useMemo(() => defaultModel(models), [models]);

  // Uncontrolled: restore last choice / fall back to the preferred default.
  useEffect(() => {
    if (controlled || !active.length) return;
    let restored: { modelId: number; slot: number } | null = null;
    try {
      restored = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
    } catch {
      /* ignore */
    }
    const m = restored && active.find((x) => x.id === restored!.modelId);
    if (m) {
      setInternalId(m.id);
      setInternalSlot(restored!.slot);
    } else if (fallback) {
      setInternalId(fallback.id);
      setInternalSlot(fallback.default_slot);
    }
  }, [controlled, active, fallback]);

  const effId = controlled ? (selection?.modelId ?? null) : internalId;
  // No model chosen yet (modelId null) = the fallback model at its own default slot.
  const effSlot = controlled
    ? selection?.modelId == null && fallback
      ? fallback.default_slot
      : (selection?.slot ?? 1)
    : internalSlot;
  const selected = active.find((m) => m.id === effId) ?? fallback;
  const preferred = active.filter((m) => m.is_preferred);
  const others = active.filter((m) => !m.is_preferred);

  // Picking a model jumps the slot to that model's default; the slot buttons
  // below override it afterwards.
  const chooseModel = (m: AnalysisModel) => {
    if (controlled) onSelectionChange?.(m.id, m.default_slot);
    else {
      setInternalId(m.id);
      setInternalSlot(m.default_slot);
    }
  };
  const chooseSlot = (s: number) => {
    if (controlled) onSelectionChange?.(effId ?? selected?.id ?? 0, s);
    else setInternalSlot(s);
  };

  const run = (model: AnalysisModel, useSlot: number) => {
    const base = resolveN8nBase();
    if (!base) {
      toastError('n8n server URL is not configured (VITE_N8N_URL).');
      return;
    }
    const url = buildModelWebhookUrl(base, model.webhook_path, {
      track_id: trackId,
      meeting_date: meetingDate,
      race_number: raceNumber,
      analysis_type_id: useSlot,
    });
    if (!controlled) {
      try {
        localStorage.setItem(LS_KEY, JSON.stringify({ modelId: model.id, slot: useSlot }));
      } catch {
        /* ignore */
      }
      setInternalId(model.id);
      setInternalSlot(useSlot);
    }
    setOpen(false);
    // Fire-and-forget: the robot webhooks answer as soon as the run starts
    // (responseMode onReceived — a request held open for the whole run was
    // re-sent by Safari when a sleeping phone woke, re-running every race), so
    // nothing here waits on the run. Toast now for confirmation.
    success(`Started ${model.label}${raceNumber ? ` on R${raceNumber}` : ''} → slot ${useSlot}.`);
    triggerWebhook(url, `${model.label} → slot ${useSlot}`);
  };

  const ModelRow = ({ m }: { m: AnalysisModel }) => (
    <button
      type="button"
      onClick={() => chooseModel(m)}
      className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent ${
        selected?.id === m.id ? 'bg-primary/10' : ''
      }`}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        {m.is_preferred ? <Star className="h-3 w-3 shrink-0 text-amber-500" /> : <span className="w-3" />}
        <span className="truncate font-medium">{m.label}</span>
        {m.no_market && <span className="shrink-0 rounded bg-muted px-1 text-[10px] text-muted-foreground">no mkt</span>}
      </span>
      <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
        slot {m.default_slot}
        {selected?.id === m.id && <Check className="h-3.5 w-3.5 text-primary" />}
      </span>
    </button>
  );

  // Only gate on there being models to run — NOT on isLoading. A run is
  // fire-and-forget, so the picker and buttons stay live while one is in-flight
  // (isLoading just drives the spinner), letting you queue a different model.
  const noModels = !active.length;
  const runVerb = runVerbOverride ?? (raceNumber ? `Analyse R${raceNumber}` : 'Analyse');

  return (
    <div className={`inline-flex flex-col items-end ${className ?? ''}`}>
      <div className="inline-flex items-stretch overflow-hidden rounded-md border border-primary/30">
        <button
          type="button"
          disabled={noModels || !selected}
          onClick={() => selected && run(selected, effSlot)}
          className="inline-flex items-center gap-1.5 bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BarChart className="h-3.5 w-3.5" />}
          {runVerb}: {selected ? selected.label : '…'} <span className="opacity-70">→ {effSlot}</span>
        </button>
        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild>
            <button
              type="button"
              disabled={noModels}
              className="border-l border-primary/40 bg-primary px-1.5 text-white hover:bg-primary/90 disabled:opacity-50"
              aria-label="Choose model and slot"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              align="end"
              sideOffset={4}
              className="z-50 w-72 rounded-md border border-border bg-popover p-2 shadow-md"
            >
              <div className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Preferred
              </div>
              {preferred.map((m) => (
                <ModelRow key={m.id} m={m} />
              ))}
              {others.length > 0 && (
                <>
                  <div className="mb-1 mt-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    More / testing
                  </div>
                  {others.map((m) => (
                    <ModelRow key={m.id} m={m} />
                  ))}
                </>
              )}
              <div className="mt-3 border-t border-border pt-2">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Slot</span>
                  <div className="inline-flex overflow-hidden rounded-md border border-border">
                    {ANALYSIS_SLOTS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => chooseSlot(s)}
                        className={`px-2.5 py-1 text-sm ${
                          effSlot === s ? 'bg-primary text-white' : 'bg-background hover:bg-accent'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={!selected}
                  onClick={() => selected && run(selected, effSlot)}
                  className="mt-1 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                >
                  {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BarChart className="h-3.5 w-3.5" />}
                  {runVerb} {selected ? selected.label : ''} → slot {effSlot}
                </button>
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>
      {message && (
        <span className={`mt-1 text-xs ${message.type === 'success' ? 'text-emerald-600' : 'text-destructive'}`}>
          {message.text}
        </span>
      )}
    </div>
  );
}
